import { Box, Chip, Link as MuiLink, Typography } from "@mui/material";
import { alpha } from "@mui/material/styles";
import { useQuery } from "@tanstack/react-query";
import { useMemo } from "react";
import type { JSX, ReactNode } from "react";
import { Link as RouterLink, useParams, useSearchParams } from "react-router-dom";

import { BackButton } from "../../components/ui/back-button.js";
import { NoAccessPanel } from "../../components/ui/no-access-panel.js";
import { PageHeader } from "../../components/ui/page-header.js";
import { PageLoader } from "../../components/ui/page-loader.js";
import { Pagination } from "../../components/ui/pagination.js";
import { usePagedList } from "../../hooks/usePagedList.js";
import { isPermissionDenied } from "../../lib/api.js";
import { listAllDocs, listCategories, listSolutionAreas } from "./api.js";
import { ArticleList, ArticleRow } from "./ArticleRow.js";
import { FolderCard, FolderGrid } from "./FolderCard.js";
import {
  isLibraryDoc,
  solutionAreasIn,
  solutionCounts,
  solutionFilter,
  solutionOptions,
  sortNewestFirst,
} from "./folder-filters.js";
import { ancestorFolders, folderCount, subFolders } from "./folder-tree.js";
import { KB_PATH, kbFolderPath } from "./kb-paths.js";
import type { DocCategory } from "./types.js";
import { useDocPdfDownload } from "./useDocPdfDownload.js";

/**
 * One folder of the Documentation section: its library-shelf articles, newest
 * first, 25 to a page, each with a Download PDF.
 *
 * The solution-area chips appear ONLY when the folder's articles span more
 * than one area — a single-area folder has nothing to switch between, and a
 * row of one chip reads as a broken control. The pick lives in `?solution=`
 * so a filtered list is shareable. There is no "Uncategorized" chip and no
 * "—" placeholder cell anywhere on the page.
 *
 * FOLDERS NEST (2026-10-31), as in the command center's tree: the page
 * opens with the folder's sub-folders as cards, then its own articles; the
 * breadcrumb walks every folder above it and Back goes to its parent. A
 * folder can be a CONTAINER — kept in the list only so an allowed sub-folder
 * sits in its place — and then it shows its sub-folders and no article.
 *
 * A slug that matches no folder the reseller may read gets a plain "not
 * shared with you" note, not an error: the folder may well exist, and the
 * honest answer is that XOP has not opened it to this company.
 */
export function CategoryPage(): JSX.Element {
  const { slug = "" } = useParams<{ slug: string }>();
  const pdf = useDocPdfDownload();
  const [searchParams, setSearchParams] = useSearchParams();
  const activeSolution = searchParams.get("solution") ?? "all";

  const cats = useQuery({ queryKey: ["docs", "categories"], queryFn: listCategories });
  const sols = useQuery({ queryKey: ["docs", "solution-areas"], queryFn: listSolutionAreas });
  const allFolders = useMemo(() => cats.data?.data ?? [], [cats.data]);
  const category = useMemo(() => allFolders.find((c) => c.slug === slug) ?? null, [allFolders, slug]);
  const ancestors = useMemo(() => (category ? ancestorFolders(category, allFolders) : []), [category, allFolders]);
  const children = useMemo(() => (category ? subFolders(category, allFolders) : []), [category, allFolders]);
  const parent = ancestors[ancestors.length - 1];

  const docList = useQuery({
    queryKey: ["docs", "folder", category?.id],
    queryFn: () => listAllDocs({ category_id: String(category?.id) }),
    enabled: category != null,
  });

  // The server filters by folder; the shelf is applied here, and the order
  // is fixed — newest first — across the whole set, BEFORE pagination.
  const articles = useMemo(() => sortNewestFirst((docList.data ?? []).filter(isLibraryDoc)), [docList.data]);
  const areas = useMemo(() => solutionAreasIn(articles, sols.data?.data ?? []), [articles, sols.data]);
  const counts = useMemo(() => solutionCounts(articles, areas), [articles, areas]);
  const options = useMemo(() => solutionOptions(counts, areas), [counts, areas]);
  const visible = useMemo(() => solutionFilter(articles, activeSolution), [articles, activeSolution]);
  const paging = usePagedList(visible, 25);

  const setSolution = (value: string): void => {
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        if (value === "all") next.delete("solution");
        else next.set("solution", value);
        return next;
      },
      { replace: true },
    );
  };

  if (cats.isLoading) return <PageLoader />;

  const error = cats.error ?? docList.error;
  if (error && isPermissionDenied(error)) {
    return <NoAccessPanel permission={error.permission ?? "documentation:view"} />;
  }

  if (!category) {
    return (
      <Box sx={{ display: "grid", gap: 2 }} data-testid="kb-folder-unknown">
        <Breadcrumb current={slug} />
        <Notice>
          <Typography sx={{ fontSize: 14, fontWeight: 500 }}>This folder isn’t shared with you.</Typography>
          <Typography sx={{ mt: 0.5, fontSize: 12, color: "text.disabled" }}>
            <MuiLink component={RouterLink} to={KB_PATH} underline="hover" sx={{ color: "inherit" }}>
              Back to Documentation
            </MuiLink>
          </Typography>
        </Notice>
      </Box>
    );
  }

  return (
    <Box sx={{ display: "grid", gap: 3 }} data-testid="kb-folder-page">
      <Box sx={{ display: "grid", gap: 1 }}>
        <PageHeader
          title={category.name}
          {...(category.description ? { description: category.description } : {})}
          actions={
            parent ? (
              <BackButton to={kbFolderPath(parent.slug)} label={parent.name} />
            ) : (
              <BackButton to={KB_PATH} label="Documentation" />
            )
          }
        />
        <Breadcrumb current={category.name} ancestors={ancestors} />
      </Box>

      {areas.length > 1 && (
        <Box
          role="group"
          aria-label="Filter by solution area"
          data-testid="solution-switcher"
          sx={{ display: "flex", flexWrap: "wrap", gap: 0.75 }}
        >
          {options.map((o) => (
            <Chip
              key={o.value}
              label={`${o.label} (${o.count})`}
              size="small"
              clickable
              onClick={() => setSolution(o.value)}
              data-testid={`solution-chip-${o.value}`}
              aria-pressed={activeSolution === o.value}
              color={activeSolution === o.value ? "primary" : "default"}
              variant={activeSolution === o.value ? "filled" : "outlined"}
            />
          ))}
        </Box>
      )}

      {children.length > 0 && (
        <Box component="section" aria-label="Folders" data-testid="kb-subfolders">
          <FolderGrid>
            {children.map((c) => (
              <FolderCard
                key={c.id}
                category={c}
                count={folderCount(c, allFolders)}
                subFolderCount={subFolders(c, allFolders).length}
              />
            ))}
          </FolderGrid>
        </Box>
      )}

      {pdf.error && (
        <Typography role="alert" sx={{ fontSize: 12, color: "error.main" }}>
          {pdf.error}
        </Typography>
      )}

      {docList.isLoading && <PageLoader />}

      {error && !docList.isLoading && (
        <Notice testId="kb-error">
          We could not load this folder: {error instanceof Error ? error.message : "unknown error"}.
        </Notice>
      )}

      {!docList.isLoading && !error && articles.length === 0 && children.length === 0 && (
        <Notice testId="kb-folder-empty">
          No articles in <Box component="strong">{category.name}</Box> yet.
        </Notice>
      )}

      {/* The folder has articles, but the active chip hides all of them. A
          distinct message, because "nothing here" and "your filter matched
          nothing" call for different next actions. */}
      {!docList.isLoading && articles.length > 0 && visible.length === 0 && (
        <Notice testId="kb-solution-empty">
          No <Box component="strong">{areas.find((a) => a.slug === activeSolution)?.name ?? activeSolution}</Box>{" "}
          articles in this folder.{" "}
          <Box
            component="button"
            type="button"
            onClick={() => setSolution("all")}
            sx={{ border: 0, bgcolor: "transparent", p: 0, font: "inherit", color: "text.primary", cursor: "pointer", textDecoration: "underline" }}
          >
            Show all
          </Box>
          .
        </Notice>
      )}

      {!docList.isLoading && visible.length > 0 && (
        <>
          <ArticleList testId="kb-articles">
            {paging.paged.map((d) => (
              <ArticleRow
                key={d.id}
                doc={d}
                pdfBusy={pdf.busySlug === d.slug}
                onPdf={() => void pdf.download(d.slug)}
              />
            ))}
          </ArticleList>
          <Pagination
            page={paging.page}
            perPage={paging.perPage}
            totalCount={paging.totalCount}
            totalPages={paging.totalPages}
            entityNoun="articles"
            hideIfSinglePage
            onPageChange={paging.setPage}
          />
        </>
      )}
    </Box>
  );
}

/** Documentation / every folder above this one / this folder. */
function Breadcrumb({
  current,
  ancestors = [],
}: {
  current: string;
  ancestors?: readonly DocCategory[];
}): JSX.Element {
  const crumbs = [{ key: "root", label: "Documentation", to: KB_PATH }, ...ancestors.map((a) => ({ key: String(a.id), label: a.name, to: kbFolderPath(a.slug) }))];
  return (
    <Box component="nav" aria-label="Breadcrumb" sx={{ fontSize: 12, color: "text.disabled" }}>
      {crumbs.map((c) => (
        <Box component="span" key={c.key}>
          <MuiLink
            component={RouterLink}
            to={c.to}
            underline="hover"
            sx={{ color: "inherit", "&:hover": { color: "text.primary" } }}
          >
            {c.label}
          </MuiLink>
          <Box component="span" sx={{ mx: 0.75 }}>
            /
          </Box>
        </Box>
      ))}
      <Box component="span" sx={{ color: "text.primary" }}>
        {current}
      </Box>
    </Box>
  );
}

function Notice({ testId, children }: { testId?: string; children: ReactNode }): JSX.Element {
  return (
    <Box
      {...(testId ? { "data-testid": testId } : {})}
      sx={{
        borderRadius: 1,
        border: 1,
        borderColor: "divider",
        bgcolor: (t) => alpha(t.palette.surfaceSoft, 0.4),
        p: 4,
        textAlign: "center",
        fontSize: 14,
        color: "text.secondary",
      }}
    >
      {children}
    </Box>
  );
}
